import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../services/api";
import RichTextEditor, {
  isRichTextEmpty,
} from "../../components/admin/RichTextEditor";
import "./admin.css";

const emptyForm = {
  title: "",
  date: "",
  tags: "",
  link: "",
  imagePreview: "",
  images: [],
  text: "",
  textBottom: "",
};

function parseDateForInput(dateStr) {
  if (!dateStr) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

  const months = {
    января: "01",
    февраля: "02",
    марта: "03",
    апреля: "04",
    мая: "05",
    июня: "06",
    июля: "07",
    августа: "08",
    сентября: "09",
    октября: "10",
    ноября: "11",
    декабря: "12",
  };

  const parts = dateStr.split(" ");
  if (parts.length !== 3) return "";
  return `${parts[2]}-${months[parts[1]]}-${String(parts[0]).padStart(2, "0")}`;
}

export default function AdminNewsForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;

    api
      .getNewsItem(id)
      .then((item) => {
        setForm({
          title: item.title || "",
          date: parseDateForInput(item.date),
          tags: item.tags || "",
          link: item.link || "",
          imagePreview: item.imagePreview || "",
          images: item.images || [],
          text: item.text || "",
          textBottom: item.textBottom || "",
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpload = async (e, field, multiple = false) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    setError("");

    try {
      const urls = [];
      for (const file of files) {
        const result = await api.uploadImage(file);
        urls.push(result.url);
      }

      if (multiple) {
        updateField(field, [...form[field], ...urls]);
      } else {
        updateField(field, urls[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (field, index) => {
    if (field === "imagePreview") {
      updateField("imagePreview", "");
      return;
    }
    updateField(
      "images",
      form.images.filter((_, i) => i !== index),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    if (isRichTextEmpty(form.text)) {
      setError("Заполните текст новости");
      setSubmitting(false);
      return;
    }

    try {
      if (isEdit) {
        await api.updateNews(id, form);
      } else {
        await api.createNews(form);
      }
      navigate("/admin/news");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="admin-page">Загрузка...</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>{isEdit ? "Редактировать новость" : "Новая новость"}</h1>
        <Link to="/admin/news" className="admin-btn">
          ← К списку
        </Link>
      </div>

      <form className="admin-form" onSubmit={handleSubmit}>
        <label>
          Заголовок *
          <input
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            required
          />
        </label>

        <label>
          Дата *
          <input
            type="date"
            value={form.date}
            onChange={(e) => updateField("date", e.target.value)}
            required
          />
        </label>

        <label>
          Теги
          <input
            value={form.tags}
            onChange={(e) => updateField("tags", e.target.value)}
            placeholder="# М-ГРУПП"
          />
        </label>

        <label>
          Ссылка на источник
          <input
            value={form.link}
            onChange={(e) => updateField("link", e.target.value)}
            placeholder="https://"
          />
        </label>

        <div className="admin-field-group">
          <label>Превью</label>
          <input
            value={form.imagePreview}
            onChange={(e) => updateField("imagePreview", e.target.value)}
            placeholder="/uploads/news/..."
          />
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleUpload(e, "imagePreview")}
          />
          {form.imagePreview && (
            <div className="admin-image-preview">
              <img src={form.imagePreview} alt="" />
              <button type="button" onClick={() => removeImage("imagePreview")}>
                Удалить
              </button>
            </div>
          )}
        </div>

        <div className="admin-field-group">
          <label>Галерея</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleUpload(e, "images", true)}
          />
          <div className="admin-gallery">
            {form.images.map((url, index) => (
              <div key={url + index} className="admin-image-preview">
                <img src={url} alt="" />
                <button
                  type="button"
                  onClick={() => removeImage("images", index)}
                >
                  Удалить
                </button>
              </div>
            ))}
          </div>
        </div>

        <RichTextEditor
          label="Текст новости *"
          value={form.text}
          onChange={(value) => updateField("text", value)}
          placeholder="Введите текст новости..."
          minHeight={320}
        />

        <RichTextEditor
          label="Текст внизу (необязательно)"
          value={form.textBottom}
          onChange={(value) => updateField("textBottom", value)}
          placeholder="Дополнительный текст внизу страницы..."
          minHeight={180}
        />

        {error && <p className="admin-error">{error}</p>}
        {uploading && <p>Загрузка изображения...</p>}

        <div className="admin-form-actions">
          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={submitting}
          >
            {submitting ? "Сохранение..." : "Сохранить"}
          </button>
          <Link to="/admin/news" className="admin-btn">
            Отмена
          </Link>
        </div>
      </form>
    </div>
  );
}
